import React, { useMemo } from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import groupBy from 'lodash/groupBy';
import { InsuranceMetaGrouping } from '@uplift/api';
import { useInsuranceCompaniesQuery } from '@uplift/queries';
import { Spinner } from '@uplift/ui-v2';

const useStyles = makeStyles({ name: 'AcceptMyInsuranceArticle' })(theme => ({
  group: {
    marginBottom: theme.spacing(2),
  },
  groupTitle: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  list: {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(0.5),
    marginBottom: 0,
  },
  listItem: {
    display: 'list-item',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  listItemText: {
    margin: 0,
  },
  paragraph: {
    marginBottom: theme.spacing(1.5),
  },
  footnote: {
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
  },
}));

const BLUE_CROSS_BLUE_SHIELD_LABEL = 'Blue Cross Blue Shield';

const GROUP_ORDER = [
  { key: 'partner', label: 'Uplift insurance partners' },
  { key: InsuranceMetaGrouping.medicare, label: 'Medicare' },
  { key: InsuranceMetaGrouping.medicaid, label: 'Medicaid' },
] as const;

const isBlueCrossBlueShield = (name: string) =>
  /blue cross blue shield/i.test(name);

const sortGroup = (key: string, companies: Array<{ name: string }>) =>
  [...companies].sort((a, b) => {
    if (key === InsuranceMetaGrouping.medicare) {
      if (a.name === 'Medicare (Original)') return -1;
      if (b.name === 'Medicare (Original)') return 1;
    }
    return a.name.localeCompare(b.name);
  });

export const AcceptMyInsuranceArticle: React.FC = () => {
  const { classes } = useStyles();
  const { data: companies = [], isLoading } = useInsuranceCompaniesQuery({ onlyAvailable: true });

  const groupedCompanies = useMemo(() => {
    const hasBlueCross = companies.some(company => isBlueCrossBlueShield(company.name));
    const withBlueCross = hasBlueCross
      ? companies
      : [
          ...companies,
          {
            id: 'blue-cross-blue-shield',
            name: BLUE_CROSS_BLUE_SHIELD_LABEL,
            metaGrouping: null,
          },
        ];

    const grouped = groupBy(withBlueCross, company => company.metaGrouping ?? 'partner');

    return GROUP_ORDER.map(({ key, label }) => ({
      key,
      label,
      companies: sortGroup(key, grouped[key] || []),
    })).filter(group => group.companies.length > 0);
  }, [companies]);

  if (isLoading) {
    return (
      <Box className={classes.loading}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" className={classes.paragraph}>
        We accept:
      </Typography>
      {groupedCompanies.map(group => (
        <Box key={group.key} className={classes.group}>
          <Typography variant="subtitle1" className={classes.groupTitle}>
            {group.label}
          </Typography>
          <List className={classes.list} disablePadding>
            {group.companies.map(company => (
              <ListItem key={company.name} className={classes.listItem} disableGutters>
                <ListItemText
                  className={classes.listItemText}
                  primary={`${company.name}${isBlueCrossBlueShield(company.name) ? '*' : ''}`}
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
      <Typography variant="body1" className={classes.paragraph}>
        More insurance carriers will be available as we work to expand. If the therapist you match with
        isn&apos;t in-network your insurance, we offer the option to pay a reduced cash rate.
      </Typography>
      <Typography variant="body2" className={classes.footnote}>
        *This does not include all Blue Cross Blue Shield plans. Please check with your plan to confirm
        that your BetterHelp sessions are covered. If you&apos;ve already scheduled your session, you can
        check your email for a message with the subject &ldquo;Your BetterHelp session costs: We&apos;ve
        verified your insurance&rdquo; to see if BetterHelp is in-network for your plan.
      </Typography>
    </Box>
  );
};
